<template>
  <div>
    <PurchaseFlowTabs
      :tabs="accreditationTabs"
      :active-index="currentSubstepIndex"
      @tab-click="handleTabClick"
    />
    <PF_Acknowledgements v-if="activeTab === 'Acknowledgements'" />
    <PF_AccreditationDocs v-else />
  </div>
</template>

<script setup>
import { computed } from "vue";
import { usePurchaseFlowStore, useDataStore, useUIStore } from "@/stores";
import PurchaseFlowTabs from "./PurchaseFlowUtilities/PurchaseFlowTabs.vue";
import PF_Acknowledgements from "./PurchaseFlowUtilities/PF_Acknowledgements.vue";
import PF_AccreditationDocs from "./PurchaseFlowUtilities/PF_AccreditationDocs.vue";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();
const UIStore = useUIStore();

const emit = defineEmits(["substep-change"]);

const accreditationTabs = [
  { name: "Acknowledgements", schema_group: null },
  { name: "Upload Documents", schema_group: null },
];

const currentSubstepIndex = computed(() => purchaseFlowStore.current_substep - 1);
const activeTab = computed(
  () => accreditationTabs[currentSubstepIndex.value]?.name || accreditationTabs[0].name
);

function handleTabClick(index) {
  emit("substep-change", index + 1);
}

function validateAcknowledgements() {
  // Check accredited investor checkbox
  if (!dataStore.contact.are_you_an_accredited_investor) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: "Please confirm that you are an accredited investor.",
    });
    return false;
  }

  const investmentType = purchaseFlowStore.current_investment.type;
  const acknowledgements = purchaseFlowStore.reg_d_acknowledgements;

  if (investmentType === "Entity") {
    // Entity: need at least one from first 14 AND at least one from last 2
    const first14 = acknowledgements.slice(0, 14);
    const last2 = acknowledgements.slice(14);

    const hasFirst14Selection = first14.some((value) => value);
    const hasLast2Selection = last2.some((value) => value);

    if (!hasFirst14Selection || !hasLast2Selection) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: "Please select at least one option from each acknowledgement section.",
      });
      return false;
    }
  } else {
    // Individual/Joint: need at least one acknowledgement
    const hasAnySelection = acknowledgements.some((value) => value);

    if (!hasAnySelection) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: "Please select at least one acknowledgement option.",
      });
      return false;
    }
  }

  return true;
}

function validateSubstep(index) {
  const tab = accreditationTabs[index];

  if (tab?.name === "Acknowledgements") {
    return {
      proceed: validateAcknowledgements(),
      error_messages: ["Invalid acknowledgements"],
      tabName: tab.name,
    };
  }

  // Upload Documents - no validation
  return {
    proceed: true,
    error_messages: [],
    tabName: tab?.name,
  };
}

defineExpose({
  async validate() {
    const result = validateSubstep(currentSubstepIndex.value);
    return result;
  },

  async validateRange(fromIndex, toIndex) {
    for (let i = fromIndex; i < toIndex; i++) {
      const result = validateSubstep(i);

      if (!result.proceed) {
        return {
          proceed: false,
          failedAt: i,
          tabName: result.tabName,
          error_messages: result.error_messages,
        };
      }
    }

    return { proceed: true };
  },
  afterContinue: async () => await purchaseFlowStore.saveAccreditationProgress(),
  substepCount: accreditationTabs.length,
});
</script>
